import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useForcedLightClass } from "./forced-light";
import { useModuleScopeProps } from "./module-scope";

const Dialog = DialogPrimitive.Root;

/** When a genie origin is provided via this context, DialogContent animates from that point. */
const DialogGenieContext = React.createContext<{ x: number; y: number } | null>(null);

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[110] bg-black/50 data-[state=open]:animate-glass-overlay-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  hideCloseButton?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideCloseButton, "aria-describedby": ariaDescribedBy, ...props }, ref) => {
  const forcedLight = useForcedLightClass();
  const genieOrigin = React.useContext(DialogGenieContext);
  // Only use genie animation on desktop (sm+) where dialog is centered
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 640;
  const useGenie = !!(genieOrigin && isDesktop);

  const genieVars = useGenie
    ? ({
        "--genie-offset-x": `${genieOrigin!.x - window.innerWidth / 2}px`,
        "--genie-offset-y": `${genieOrigin!.y - window.innerHeight / 2}px`,
      } as React.CSSProperties)
    : {};
  // The dialog is portaled out of the page, so the page's module accent is handed to it (#7912).
  const moduleScope = useModuleScopeProps(props.style ?? genieVars);

  return (
    <DialogPortal>
      <DialogOverlay />
      {/* The dialog's one scroll container (#7329): it owns the max-height and the scrolling.
          Nothing inside a canonical dialog scrolls on its own; see src/components/Modal/ModalLayout.tsx.
          The cap is the --dialog-max-height token (src/styles/tokens.css): 85dvh, or 85vh where the
          browser has no dvh unit. It is set twice, plain and `sm:`, so a call site's own `max-h-*`
          still replaces only the phone cap and the shared cap holds from `sm` up, as it always has. */}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed bottom-0 left-[50%] z-[110] grid w-full max-w-lg translate-x-[-50%] gap-4 rounded-t-[20px] border border-border bg-surfaceRaised shadow-soft-lg overflow-x-hidden p-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[var(--dialog-max-height)] overflow-y-auto sm:top-[50%] sm:bottom-auto sm:translate-y-[-50%] sm:rounded-[20px] sm:p-6 sm:pb-6 sm:max-h-[var(--dialog-max-height)]",
          useGenie
            ? "data-[state=open]:animate-quick-add-genie-in data-[state=closed]:animate-quick-add-genie-out"
            : "data-[state=open]:animate-glass-panel-in data-[state=closed]:animate-glass-panel-out",
          className,
          forcedLight,
        )}
        style={genieVars}
        aria-describedby={ariaDescribedBy ?? undefined}
        {...props}
        {...moduleScope}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-full liquid-glass-close p-1.5 opacity-80 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

// Forwards its ref so ModalFooter can measure itself (see src/components/Modal/ModalLayout.tsx).
const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
      {...props}
    />
  ),
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogGenieContext,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
